import React, { useEffect } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { setTableParams } from 'reducers/tableParams';
import { createUserFilterAction, updateUserFilterAction } from 'reducers/userFilters';
// components
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router';
import DataTable from 'components/DataTable/DataTable';
import { CustomColumn } from 'components/DataTable/Columns';
import { CombinedBadge, ScheduledTreatmentStatusBadge } from 'components/StatusBadge/StatusBadge';
import Panel from 'components/Panel';
import Preloader from 'components/Preloader';
import NothingBox from 'components/NothingBox';
import ScheduleViewShell from './ScheduleViewShell';
// hooks
import useDataTable, { tableParamsSelector } from 'hooks/useDataTable';
// utils
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import { formatDateRangeForUser } from 'utils/regionalDateHelper';
import { tableNames } from 'utils/constants';
import { allOrAnyTreatmentsOptions, userFilterGroupNames, userFilterSectionTypes } from 'utils/constants/userFilters';
// api
import { fetchScheduledTreatments, applyScheduledTreatmentsFilter } from 'endpoints/mySchedule';
// styles
import styles from '../MySchedule.module.scss';

// 14-day rolling window by default, adjustable via the filter bar
const initialFilter = {
  sections: [{ type: 'ProtocolDate', condition: 'lt_days_in', key: 1, options: [{ key: 'days', value: 14 }] }],
  type: 'ProtocolDate',
};

const TreatmentsView = ({
  isOnline,
  setTableParams,
  tableParams,
  tabs,
  createUserFilterAction,
  updateUserFilterAction,
  countryCode,
}) => {
  const { formatMessage } = useIntl();
  const {
    data: { resources, isLoading, meta: { total } },
    fetchData,
    onPageChange,
    onPerPageChange,
    onSortChange,
    onUserFilterSectionsChange,
    onUserFilterSegmentSave,
  } = useDataTable(fetchScheduledTreatments, {
    setTableParams: (params) => setTableParams(tableNames.myScheduleTreatments, params),
    tableParams,
    filterFetcher: applyScheduledTreatmentsFilter,
    userFilterGroupName: userFilterGroupNames.myScheduleTreatments,
    createUserFilter: createUserFilterAction,
    updateUserFilter: updateUserFilterAction,
  });
  const isFiltersTouched = 'filters' in tableParams;
  const { page, per_page, sort } = tableParams;

  const sections = [
    { value: userFilterSectionTypes.farm, label: formatMessage({ id: 'general.farmName' }) },
    { value: userFilterSectionTypes.protocolDate, label: formatMessage({ id: 'general.protocolDate' }) },
  ];

  useEffect(() => {
    if (isOnline) {
      fetchData({
        ...tableParams,
        sort: tableParams.sort || 'protocol_start_date asc',
        filters: isFiltersTouched ? tableParams.filters : [initialFilter],
      }).catch(toastResponseErrors);
    }
  }, [isOnline]);

  // Flexible Start protocols show a date range until the first administration locks the dates
  const formatDateRange = (from, to) => (
    from ? formatDateRangeForUser(from, to, countryCode) : <FormattedMessage id="general.notAvail" />
  );

  // DataTable toggles the direction of the current sort string even when another column
  // is clicked; a newly clicked column should always start ascending
  const handleSortChange = (newSort) => {
    const [newField] = newSort.split(' ');
    const [currentField] = (sort || 'protocol_start_date asc').split(' ');
    onSortChange(newField === currentField ? newSort : `${newField} asc`);
  };

  const renderNameColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="component.dataTable.headers.name" />}>
      <div>
        <div>{rowData.name}</div>
        {rowData.combined && <CombinedBadge className="mt-5" />}
      </div>
    </CustomColumn>
  );

  const renderSourceFarmColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="general.sourceFarm" />}>
      {rowData.farm?.id ? (
        <Link className="primary" to={`/barnsheets/farms/${rowData.farm.id}`}>
          {rowData.farm.name}
        </Link>
      ) : (
        <FormattedMessage id="general.notAvail" />
      )}
    </CustomColumn>
  );

  const renderSourceGroupColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="general.sourceGroup" />}>
      {rowData.pig_group?.id ? (
        <Link className="primary" to={`/barnsheets/groups/${rowData.pig_group.id}/treatments`}>
          {rowData.pig_group.name}
        </Link>
      ) : (
        <FormattedMessage id="general.notAvail" />
      )}
    </CustomColumn>
  );

  const renderMedicationsColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="general.medications" />}>
      {rowData.medications?.length ? (
        <div>
          {rowData.medications.map((medication) => <div key={medication}>{medication}</div>)}
        </div>
      ) : (
        <FormattedMessage id="general.notAvail" />
      )}
    </CustomColumn>
  );

  // Combined rows repeat the window per member protocol, single rows render one line
  const renderStartDateColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="general.protocolStartDate" />}>
      <div>
        {(rowData.protocols || []).map((protocol) => (
          <div key={protocol.id}>
            {formatDateRange(protocol.protocol_start_date, protocol.protocol_start_date_to)}
          </div>
        ))}
      </div>
    </CustomColumn>
  );

  const renderEndDateColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="general.protocolEndDate" />}>
      <div>
        {(rowData.protocols || []).map((protocol) => (
          <div key={protocol.id}>
            {formatDateRange(protocol.protocol_end_date, protocol.protocol_end_date_to)}
          </div>
        ))}
      </div>
    </CustomColumn>
  );

  const renderStatusColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="component.dataTable.headers.status" />}>
      <ScheduledTreatmentStatusBadge status={rowData.status} />
    </CustomColumn>
  );

  const columns = [
    {
      label: <FormattedMessage id="component.dataTable.headers.name" />,
      flex: '1 1 160px',
      sortKey: 'name',
      renderer: renderNameColumn,
    },
    {
      label: <FormattedMessage id="general.sourceFarm" />,
      flex: '1 1 140px',
      sortKey: 'farm_name',
      renderer: renderSourceFarmColumn,
    },
    {
      label: <FormattedMessage id="general.sourceGroup" />,
      flex: '1 1 140px',
      sortKey: 'group_name',
      renderer: renderSourceGroupColumn,
    },
    {
      label: <FormattedMessage id="general.medications" />,
      flex: '1 1 180px',
      sortKey: 'medications',
      renderer: renderMedicationsColumn,
    },
    {
      label: <FormattedMessage id="general.protocolStartDate" />,
      flex: '1 1 120px',
      sortKey: 'protocol_start_date',
      renderer: renderStartDateColumn,
    },
    {
      label: <FormattedMessage id="general.protocolEndDate" />,
      flex: '1 1 120px',
      sortKey: 'protocol_end_date',
      renderer: renderEndDateColumn,
    },
    {
      label: <FormattedMessage id="component.dataTable.headers.status" />,
      flex: '0 0 110px',
      sortKey: 'status',
      renderer: renderStatusColumn,
    },
  ];

  const paginationProps = {
    onPageChange,
    onPerPageChange,
    totalItems: total,
    currentPage: page,
    perPage: per_page,
  };

  return (
    <ScheduleViewShell
      allOrAnyConditionLabel={<FormattedMessage id="component.advancedSearchItem.allTreatmentsFilters" />}
      fetchData={fetchData}
      filterGroupName={userFilterGroupNames.myScheduleTreatments}
      filterSections={sections}
      initialFilter={initialFilter}
      isFiltersTouched={isFiltersTouched}
      listOptions={allOrAnyTreatmentsOptions}
      onUserFilterSectionsChange={onUserFilterSectionsChange}
      onUserFilterSegmentSave={onUserFilterSegmentSave}
      tableParams={tableParams}
    >
      <Panel className={styles['schedule-panel']}>
        <Panel.Heading
          title={<FormattedMessage id="general.pageTitle.mySchedule" />}
        >
          {tabs && <div className={styles['schedule-tabs']}>{tabs}</div>}
        </Panel.Heading>
        <Panel.Body noPadding>
          <Preloader isActive={isLoading} />
          <DataTable
            className={styles['treatments-table']}
            data={resources}
            columns={columns}
            sort={sort || ''}
            onSortChange={handleSortChange}
            scrollable
            isLoading={isLoading}
            paginationProps={paginationProps}
            rowKey="row_key"
          />
          <NothingBox display={!resources.length} isLoading={isLoading}>
            <h2 className="lighter semibold">
              <FormattedMessage id="component.nothingBox.noScheduledTreatments" />
            </h2>
          </NothingBox>
        </Panel.Body>
      </Panel>
    </ScheduleViewShell>
  );
};

TreatmentsView.propTypes = {
  isOnline: T.bool.isRequired,
  tableParams: T.object.isRequired,
  setTableParams: T.func.isRequired,
  tabs: T.node,
  createUserFilterAction: T.func.isRequired,
  updateUserFilterAction: T.func.isRequired,
  countryCode: T.string,
};

export default connect(
  (state) => ({
    tableParams: tableParamsSelector(state, tableNames.myScheduleTreatments),
    isOnline: state.network.isOnline,
    countryCode: getCurrentCountryCode(state),
  }), {
    setTableParams,
    createUserFilterAction,
    updateUserFilterAction,
  }
)(TreatmentsView);
